import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AppUrlConstants } from '../constants/AppUrlConstants';
import {
  EmailCategory,
  EmailCounts,
  EmailFilters,
  EmailListItem,
  PagedResponse,
  EmailThread,
  DraftDetail,
  AdminComplaintSubmitRequest,
  AdminComplaintSubmitResponse,
  AdminReplyResponse,
  AdminRejectResponse,
  EmailAttachment
} from '../models/email.model';

@Injectable({
  providedIn: 'root'
})
export class EmailsService {

  private readonly urlMap: Record<EmailCategory, string> = {
    'all':          AppUrlConstants.ALL_EMAILS_URL,
    'black-listed': AppUrlConstants.BLACKLISTED_EMAILS_URL,
    'grey-listed':  AppUrlConstants.GREYLISTED_EMAILS_URL,
    'white-listed': AppUrlConstants.WHITELISTED_EMAILS_URL,
  };

  private readonly skipSpinner = new HttpHeaders({ 'X-Skip-Spinner': 'true' });

  constructor(private http: HttpClient) { }

  getEmails(
    category: EmailCategory,
    page: number = 0,
    size: number = 25,
    filters: EmailFilters = {},
    sortBy: string = 'receivedAt',
    sortDir: 'asc' | 'desc' = 'desc'
  ): Observable<PagedResponse<EmailListItem>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sortBy', sortBy)
      .set('sortDir', sortDir);

    if (filters.from)         params = params.set('from', filters.from);
    if (filters.to)           params = params.set('to', filters.to);
    if (filters.search)       params = params.set('search', filters.search);
    if (filters.intentGroup)  params = params.set('intentGroup', filters.intentGroup);
    if (filters.needsReview != null) params = params.set('needsReview', String(filters.needsReview));

    if (category === 'grey-listed' && filters.greylistType && filters.greylistType !== 'ALL') {
      params = params.set('type', filters.greylistType);
    }
    if (category === 'black-listed' && filters.reason) {
      params = params.set('reason', filters.reason);
    }

    return this.http.get<PagedResponse<EmailListItem>>(this.urlMap[category], { params, headers: this.skipSpinner });
  }

  getCounts(from?: string, to?: string): Observable<EmailCounts> {
    let params = new HttpParams();
    if (from) params = params.set('from', from);
    if (to)   params = params.set('to', to);
    return this.http.get<EmailCounts>(AppUrlConstants.EMAIL_COUNTS_URL, { params, headers: this.skipSpinner });
  }

  getEmailThread(draftId: number): Observable<EmailThread> {
    const url = `${AppUrlConstants.EMAIL_BASE_URL}/${draftId}/thread`;
    return this.http.get<EmailThread>(url, { headers: this.skipSpinner });
  }

  /** Thread keyed by intake id — works for blacklisted intakes that have no draft. */
  getIntakeThread(intakeId: number): Observable<EmailThread> {
    const url = `${AppUrlConstants.EMAIL_BASE_URL}/intake/${intakeId}/thread`;
    return this.http.get<EmailThread>(url, { headers: this.skipSpinner });
  }

  getDraftDetail(intakeId: number): Observable<DraftDetail> {
    const params = new HttpParams().set('intakeId', intakeId.toString());
    return this.http.get<DraftDetail>(AppUrlConstants.EMAIL_DRAFT_URL, { params, headers: this.skipSpinner });
  }

  submitDraft(draftId: number, request: AdminComplaintSubmitRequest): Observable<AdminComplaintSubmitResponse> {
    const url = `${AppUrlConstants.EMAIL_BASE_URL}/admin/drafts/${draftId}/submit`;
    return this.http.post<AdminComplaintSubmitResponse>(url, request, { headers: this.skipSpinner });
  }

  /** Admin reply to the citizen — targets the ORIGINAL message's intake id. */
  sendAdminReply(intakeId: number, body: string, attachments?: File[]): Observable<AdminReplyResponse> {
    const url = `${AppUrlConstants.EMAIL_BASE_URL}/admin/intake/${intakeId}/reply`;
    const formData = new FormData();
    formData.append('body', body);
    (attachments || []).forEach(file => formData.append('attachments', file, file.name));
    return this.http.post<AdminReplyResponse>(url, formData, { headers: this.skipSpinner });
  }

  /** Move a greylisted email to the black list with a reason. */
  rejectIntake(intakeId: number, reason: string): Observable<AdminRejectResponse> {
    const url = `${AppUrlConstants.EMAIL_BASE_URL}/admin/intake/${intakeId}/reject`;
    const params = new HttpParams().set('reason', reason);
    return this.http.post<AdminRejectResponse>(url, null, { params, headers: this.skipSpinner });
  }

  getAttachments(intakeId: number): Observable<EmailAttachment[]> {
    const url = `${AppUrlConstants.EMAIL_BASE_URL}/intake/${intakeId}/attachments`;
    return this.http.get<EmailAttachment[]>(url, { headers: this.skipSpinner });
  }

  getAttachmentDownloadUrl(attachmentId: number): string {
    return `${AppUrlConstants.EMAIL_BASE_URL}/attachments/${attachmentId}/download`;
  }
}
